/**
 * Entitlement hook + upgrade modal — Patch 25.
 *
 * The ONLY canonical way frontend features check entitlements. No
 * feature module may branch on plan_key — always call ``useEntitlements()``
 * and read the ``capabilities`` / ``remaining`` fields.
 */
import { create } from "zustand";
import { useQuery } from "@tanstack/react-query";
import { http } from "@/shared/api/http";


export interface EntitlementSnapshot {
  workspace_id: string;
  plan: {
    key: string;
    display_name: string;
    price_usd: number | null;
    price_suffix: string;
    recommended: boolean;
    tagline: string;
    limits: {
      workspaces: number | null;
      team_seats: number | null;
      storage_gb: number | null;
      text_generations: number | null;
      premium_images_per_month: number | null;
      premium_videos_per_month: number | null;
      voice_minutes_per_month: number | null;
    };
    capabilities: string[];
  };
  capabilities: string[];
  remaining: {
    workspaces: number | null;
    team_seats: number | null;
    storage_gb: number | null;
    premium_images: number | null;
    premium_videos: number | null;
    voice_minutes: number | null;
    text_generations: number | null;
  };
  usage: Record<string, number>;
  add_ons: Record<string, number>;
}


async function fetchEntitlements(): Promise<EntitlementSnapshot | null> {
  try {
    return await http<EntitlementSnapshot>("/entitlements");
  } catch {
    return null;
  }
}


export function useEntitlements() {
  const q = useQuery({
    queryKey: ["entitlements"],
    queryFn: fetchEntitlements,
    refetchInterval: 90_000,
    retry: false,
  });

  const snap = q.data ?? null;
  const has = (cap: string): boolean => Boolean(snap?.capabilities.includes(cap));
  const remaining = (k: keyof EntitlementSnapshot["remaining"]): number | null =>
    snap?.remaining[k] ?? null;

  return {
    snapshot: snap,
    isLoading: q.isLoading,
    // Capability probes — feature code calls these, never checks plan.
    canUseAutopilot: () => has("autopilot"),
    canPublishTikTok: () => has("publish.tiktok"),
    canPublishYouTube: () => has("publish.youtube"),
    canPublishLinkedIn: () => has("publish.linkedin"),
    canUseEmailCampaigns: () => has("email_campaigns"),
    canUseBusinessBrain: () => has("business_brain"),
    canUseAnalytics: () => has("analytics"),
    canUseCreativeDirector: () => has("creative_director"),
    hasTeamCollaboration: () => has("team_collaboration"),
    has,
    // Quota probes.
    remainingImages: () => remaining("premium_images"),
    remainingVideos: () => remaining("premium_videos"),
    remainingVoiceMinutes: () => remaining("voice_minutes"),
    remainingWorkspaces: () => remaining("workspaces"),
    remainingTeamSeats: () => remaining("team_seats"),
    remaining,
    // Patch 26 — capacity probes used by MediaPage to render preview locks.
    // A capacity is "available" when the plan's monthly limit is > 0 OR the
    // limit is unlimited (null). Free plan videos/voice return false.
    canGenerateVideo: () => {
      const lim = snap?.plan?.limits?.premium_videos_per_month;
      return lim === null || (typeof lim === "number" && lim > 0);
    },
    canGenerateImage: () => {
      const lim = snap?.plan?.limits?.premium_images_per_month;
      return lim === null || (typeof lim === "number" && lim > 0);
    },
    canGenerateVoice: () => {
      const lim = snap?.plan?.limits?.voice_minutes_per_month;
      return lim === null || (typeof lim === "number" && lim > 0);
    },
    refetch: q.refetch,
  };
}


// ── Upgrade modal store ───────────────────────────────────────────
interface UpgradePrompt {
  title: string;
  reason: string;
  addon_kind?: string;
  upgrade_target?: string;
}

interface UpgradeStore {
  prompt: UpgradePrompt | null;
  open: (p: UpgradePrompt) => void;
  close: () => void;
}

export const useUpgradePrompt = create<UpgradeStore>((set) => ({
  prompt: null,
  open: (p) => set({ prompt: p }),
  close: () => set({ prompt: null }),
}));
